"use client"

import { motion } from "@/components/motion/framer"

interface IdeaToExecutionProps {
  className?: string
}

export function IdeaToExecution({ className }: IdeaToExecutionProps) {
  // Check if this is a small container (like in service cards)
  const isSmall = className?.includes('h-32') || className?.includes('h-24') || className?.includes('h-20')
  const scaleClass = isSmall ? '' : 'scale-150 sm:scale-[2.5]'
  
  return (
    <div className={`relative h-full w-full ${className || ""}`}>
      <div className={`absolute inset-0 flex items-center justify-center gap-4 ${scaleClass}`}>
        {/* Idea - left side */}
        <motion.div
          className="relative h-20 w-16 rounded-lg border-2 border-tk-teal/50 bg-tk-teal/5"
          animate={{ opacity: [0.7, 1, 0.7] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          {/* Checklist/idea symbol */}
          <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 p-2">
            {/* Checklist items */}
            {[
              { checked: true, delay: 0 },
              { checked: true, delay: 0.2 },
              { checked: false, delay: 0.4 },
            ].map((item, i) => (
              <motion.div
                key={i}
                className="flex w-full items-center gap-1.5"
                initial={{ opacity: 0, x: -5 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: item.delay, duration: 0.3 }}
              >
                {/* Checkbox */}
                <motion.div
                  className={`h-2.5 w-2.5 rounded border ${
                    item.checked
                      ? "border-tk-teal/60 bg-tk-teal/20"
                      : "border-tk-slate/40"
                  }`}
                  animate={{
                    scale: item.checked ? [1, 1.1, 1] : 1,
                  }}
                  transition={{ duration: 1.5, delay: item.delay, repeat: Infinity }}
                >
                  {/* Checkmark */}
                  {item.checked && (
                    <motion.svg
                      className="h-full w-full text-tk-teal/70"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}
                    >
                      <motion.path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.3, delay: item.delay + 0.1 }}
                      />
                    </motion.svg>
                  )}
                </motion.div>
                {/* Text line */}
                <motion.div
                  className={`h-1 flex-1 rounded ${
                    item.checked ? "bg-tk-teal/30" : "bg-tk-slate/30"
                  }`}
                  animate={{
                    opacity: item.checked ? [0.6, 0.8, 0.6] : [0.3, 0.5, 0.3],
                  }}
                  transition={{ duration: 1.5, delay: item.delay, repeat: Infinity }}
                />
              </motion.div>
            ))}
          </div>
          {/* Arrow pointing right */}
          <motion.div
            className="absolute -right-3 top-1/2 -translate-y-1/2 text-tk-teal/50"
            animate={{ x: [0, 3, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
          >
            →
          </motion.div>
        </motion.div>

        {/* Execution - right side - App screen */}
        <motion.div
          className="relative h-24 w-14 rounded-[12px] border-2 border-tk-teal/60 bg-gradient-to-br from-tk-linen to-tk-teal/5 shadow-lg"
          animate={{ 
            opacity: [0.9, 1, 0.9],
            scale: [0.98, 1, 0.98]
          }}
          transition={{ duration: 2, delay: 0.3, repeat: Infinity }}
        >
          {/* Mobile app screen */}
          <div className="flex h-full w-full flex-col">
            {/* Status bar */}
            <div className="flex h-2 w-full items-center justify-between border-b border-tk-slate/10 bg-tk-slate/5 px-1.5">
              <div className="flex gap-0.5">
                <div className="h-0.5 w-2 rounded-full bg-tk-slate/40" />
                <div className="h-0.5 w-1 rounded-full bg-tk-slate/30" />
              </div>
              <div className="h-1 w-3 rounded border border-tk-slate/20" />
              <div className="h-0.5 w-2 rounded-full bg-tk-teal/60" />
            </div>
            
            {/* App content */}
            <div className="flex flex-1 flex-col gap-1 p-1.5">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="h-1.5 w-8 rounded bg-tk-slate/20" />
                <div className="h-1.5 w-1.5 rounded-full bg-tk-teal/40" />
              </div>
              
              {/* Content cards */}
              <div className="flex flex-1 flex-col gap-1">
                <div className="h-2.5 w-full rounded bg-gradient-to-r from-tk-teal/20 to-tk-teal/10">
                  <div className="flex h-full items-center gap-1 p-0.5">
                    <div className="h-1.5 w-1.5 rounded bg-tk-teal/40" />
                    <div className="flex-1">
                      <div className="h-0.5 w-3/4 rounded bg-tk-slate/20" />
                      <div className="mt-0.5 h-0.5 w-1/2 rounded bg-tk-slate/15" />
                    </div>
                  </div>
                </div>
                <div className="h-2 w-full rounded bg-tk-slate/10">
                  <div className="h-0.5 w-2/3 rounded bg-tk-slate/20" />
                </div>
              </div>
              
              {/* Bottom navigation */}
              <div className="flex h-1.5 items-center justify-around border-t border-tk-slate/10 pt-0.5">
                {[0, 1, 2, 3].map((i) => (
                  <div key={i} className="h-1 w-1 rounded-full bg-tk-slate/30" />
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  )
}

